// ============================================================
// 티켓 투 라이드 — Play 페이지 본문 (SEQ=10146 UX 본문)
//   라우트(SoT §3-1): /play/ticket/:roomCode (App.tsx page-state 'play_ticket')
//   카탄 pages/Play.tsx 패턴 — authState/roomCode/matchId props + hub start + unmount stop.
//
//   변경 사항 vs SEQ=10143 스켈레톤:
//     1) 컴포넌트 분리 (TtrBoard / TtrHand / TtrActionPanel / TtrPlayersPanel)
//     2) 모달 3종 통합 (ClaimRoute / TicketKeep / GameEnd)
//     3) 본인 user_code 주입 (ticketStore.setMyUserCode) — BFS 라이브 갱신 가동
//     4) 에러 토스트 (TtrActionError broadcast 수신 → 일시 표시)
//     5) 강조 도시 ring (선택한 티켓 src/dst 동기)
//     6) AWAIT_* 서브 상태 분기 (액션 패널 + 모달 자동 표시)
//     7) keep invoke 직후 낙관적 로컬 반영 (applyMyTicketsKeptLocal)
// ============================================================

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AuthState } from '../store/auth'
import {
  ticketStore,
  subscribeTicket,
  TtrRoute,
} from '../store/ticket'
import * as ticketHub from '../hub/ticketHub'
import * as roomHub   from '../hub/roomHub'
import { apiTtrGameState } from '../server/ServiceCall'
import TtrBoard           from '../components/TtrBoard'
import TtrHand            from '../components/TtrHand'
import TtrActionPanel     from '../components/TtrActionPanel'
import TtrPlayersPanel    from '../components/TtrPlayersPanel'
import TtrClaimRouteModal from '../components/TtrClaimRouteModal'
import TtrTicketKeepModal from '../components/TtrTicketKeepModal'
import TtrGameEndModal    from '../components/TtrGameEndModal'
import ChatPanel          from '../components/ChatPanel'
import { roomStore, ChatEntry, subscribeRoom } from '../store/room'
import { sound } from '../util/sound'
import '../css/App.css'
import '../css/TtrPlayTicket.css'

export interface PlayTicketProps {
  authState:  AuthState
  roomCode:   string
  matchId?:   number
  onLeave:    () => void
  onLogout:   () => void
}

const BOARD_WIDTH  = 1024
const BOARD_HEIGHT = 768

const PlayTicket: React.FC<PlayTicketProps> = ({
  authState,
  roomCode,
  matchId,
  onLeave,
  onLogout,
}) => {
  // ── store 구독 (카탄 Play 패턴) ──────────────────────────
  const [, setTick] = useState(0)
  useEffect(() => {
    const unsub = subscribeTicket(() => setTick(t => t + 1))
    return unsub
  }, [])

  const game             = ticketStore.getGame()
  const players          = ticketStore.getPlayers()
  const cities           = ticketStore.getCities()
  const routes           = ticketStore.getRoutes()
  const claims           = ticketStore.getClaims()
  const faceUp           = ticketStore.getFaceUp()
  const deckRemain       = ticketStore.getDeckRemain()
  const ticketDeckRemain = ticketStore.getTicketDeckRemain()
  const myHand           = ticketStore.getMyHandSummary()
  const myTickets        = ticketStore.getMyTickets()
  const myPending        = ticketStore.getMyPendingTickets()
  const otherCounts      = ticketStore.getOtherCounts()
  const gameEnded        = ticketStore.getGameEnded()
  const subPhase         = ticketStore.getMySubPhase()
  // SEQ=10199 STEP=1: 한 턴 1액션 가드 (서버 ACTION_DONE_THIS_TURN 미러)
  const actionDoneThisTurn = ticketStore.getActionDoneThisTurn()
  // SEQ=10200 STEP=1: catch #11 노선 클레임 시각 강화 — 최근 점유 lane (1.5초)
  const recentClaim        = ticketStore.getRecentClaim()

  const isMyTurn = game?.currentTurnUserCode === authState.userCode
  // SEQ=10211 STEP=20 (root cause fix): 새로고침 후 App.tsx props.matchId가 undefined로 전달되는 케이스 fallback.
  //   ticketStore.game.matchId(GameLoad hydrate)로 자동 보완 → button onClick 첫 가드 통과 보장.
  const effectiveMatchId: number | undefined = (matchId && matchId > 0) ? matchId : (game?.matchId && game.matchId > 0 ? game.matchId : undefined)

  // 본인 user_code 주입 (BFS 라이브 갱신 가동) — 1회
  useEffect(() => {
    ticketStore.setMyUserCode(authState.userCode)
  }, [authState.userCode])

  // SEQ=10212 — 본인 턴 진입 효과음 (사용자 요청: 카탄 trade_request 사운드 재활용)
  //   카탄 Play.tsx L590~601 패턴 미러. 첫 hydrate(prev=undefined)는 skip → 새로고침/자동복귀 시 잔재 재생 차단.
  //   순수 턴 전환(상대 → 본인) 시점만 재생.
  const prevTurnUserRef = useRef<number | null | undefined>(undefined)
  useEffect(() => {
    const turnUc = game?.currentTurnUserCode
    if (turnUc !== prevTurnUserRef.current) {
      if (prevTurnUserRef.current !== undefined && turnUc === authState.userCode) {
        // SEQ=10214 STEP=1 (사용자 catch "trade_request 귀에 안 들어옴"): my_turn 음원으로 교체.
        // 카탄 본인 턴 사운드와 의미적으로도 정합 — 동일 음원 공유.
        sound.play('my_turn')
      }
      prevTurnUserRef.current = turnUc
    }
  }, [game?.currentTurnUserCode, authState.userCode])

  // SEQ=10213 STEP=1 — 사운드 음소거 토글 (사용자 요청). localStorage 'board.muted' 영구화는 sound 매니저 내부 처리.
  const [muted, setMuted] = useState<boolean>(sound.isMuted())
  const handleToggleMute = () => {
    const next = sound.toggleMuted()
    setMuted(next)
  }

  // ── 채팅 state — useEffect 위로 hoist 필요 (TDZ 회피, SEQ=10211 STEP=10) ──
  const [chatLog, setChatLog]   = useState<ChatEntry[]>(roomStore.getChatLog())
  const [showChat, setShowChat] = useState<boolean>(false)
  const [unreadChat, setUnreadChat] = useState<number>(0)
  const prevChatLen = useRef<number>(roomStore.getChatLog().length)

  // ── ticketHub 연결 준비 상태 (SEQ=10211 STEP=16, 사용자 catch "새로고침하면 버튼 먹통")
  //   가설 A: startConnection 비동기 미완 상태에서 button onClick → invoke silent fail.
  //   hubReady=false 시 모든 액션 button disabled + 헤더 "연결 중" 안내. true 시 정상 활성.
  const [hubReady, setHubReady] = useState<boolean>(false)

  // ── Hub 연결 / 해제 + 초기 하이드레이션 (SEQ=10147) ──────
  //   1) REST PID 21101 apiTtrGameState 호출 → 정적 마스터(cities/routesMaster/ticketsMaster) + 10 결과셋 hydrate
  //   2) ticketHub.startConnection 후 broadcast 이벤트 wire-up
  //   3) 재연결 시 ticketHub.gameLoad(matchId) invoke 로 권위 hydration (Hub.GameLoad)
  useEffect(() => {
    let cancelled = false

    // 1) REST 통합 로드 (정적 마스터 + 게임 상태)
    apiTtrGameState(authState.token, roomCode, matchId ?? 0)
      .then(payload => {
        if (cancelled || !payload) return
        const cities        = (payload.cities        as Array<{ cityId: number; name: string; displayX: number; displayY: number; importance?: number }>) ?? []
        const routesMaster  = (payload.routesMaster  as Array<{ routeId: number; cityAId: number; cityBId: number; color: string; length: number; laneNo: number; isDouble: boolean }>) ?? []
        const ticketsMaster = (payload.ticketsMaster as Array<{ ticketId: number; cityAId: number; cityBId: number; points: number; isLong: boolean }>) ?? []
        // 정적 마스터 로드 (TtrCity/TtrRoute/TtrTicket 형으로 그대로 매핑)
        ticketStore.applyStaticMasters(cities, routesMaster, ticketsMaster)
        // 게임 상태 (10 결과셋) — applyGameState 가 matchHeader/players/faceUpCards/myHand/myTickets/cardCounts 등 매핑
        ticketStore.applyGameState(payload)
      })
      .catch(e => console.error('[PlayTicket] apiTtrGameState 초기 로드 실패', e))

    // 2) Hub 연결 + 재연결 시 권위 hydration
    setHubReady(false)
    ticketHub.startConnection(authState.token).then(() => {
      if (cancelled) {
        ticketHub.stopConnection().catch(() => {})
        return
      }
      // SEQ=10211 STEP=16: 연결 완료 → button 활성화 신호 (가설 A fix)
      setHubReady(true)
      // 3) Hub.GameLoad invoke 시도 — 진행중 게임이 있으면 GameLoadCaller 가 payload 반환
      //    실패 시 silent (이미 REST 로 hydrate 되었음)
      if (matchId && matchId > 0) {
        ticketHub.gameLoad(matchId).catch(e => {
          console.warn('[PlayTicket] hub.gameLoad invoke 실패 (재연결 권위 hydration 생략)', e)
        })
      }
    }).catch(e => {
      console.error('[PlayTicket] hub 시작 실패', e)
      if (!cancelled) {
        setErrorToast('서버 연결 실패 — 새로고침 후 재시도')
        // hubReady false 유지 → button 모두 disabled
      }
    })

    // SEQ=10211 STEP=10: RoomHub 연결 + 채팅 wire (카탄 Play.tsx L1117~1162 패턴 미러)
    const onRoomChatReceived = (data: unknown) => {
      const d = data as { userCode: number; nickname: string; message: string; timestamp: string }
      roomStore.appendChat({
        userCode:  d.userCode,
        nickname:  d.nickname,
        message:   d.message,
        timestamp: (d.timestamp ?? '').slice(11, 16),
        isSystem:  false,
      })
    }
    const onRoomMemberJoined = (data: unknown) => {
      const d = data as { nickname: string }
      if (d.nickname) roomStore.appendSystemChat(`${d.nickname} 님이 입장했습니다.`, 'MEMBER_ENTER')
    }
    const onRoomMemberLeft = (data: unknown) => {
      const d = data as { userCode: number }
      const leftNick = players.find(p => p.userCode === d.userCode)?.nickname
      if (leftNick) roomStore.appendSystemChat(`${leftNick} 님이 퇴장했습니다.`, 'MEMBER_LEAVE')
    }
    roomHub.startConnection(authState.token).catch(() => { /* 채팅만 비활성, 게임 진행 가능 */ })
    roomHub.on('ChatReceived', onRoomChatReceived  as (...a: unknown[]) => void)
    roomHub.on('MemberJoined', onRoomMemberJoined  as (...a: unknown[]) => void)
    roomHub.on('MemberLeft',   onRoomMemberLeft    as (...a: unknown[]) => void)

    return () => {
      cancelled = true
      // 페이지 떠날 때 종료 (카탄 패턴 — 매치 진행 중 다른 페이지 잠시 이동 시 stop 보류 가능, 추후 결정)
      ticketHub.stopConnection().catch(() => {})
      roomHub.off('ChatReceived', onRoomChatReceived  as (...a: unknown[]) => void)
      roomHub.off('MemberJoined', onRoomMemberJoined  as (...a: unknown[]) => void)
      roomHub.off('MemberLeft',   onRoomMemberLeft    as (...a: unknown[]) => void)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState.token, roomCode, matchId])

  // ── 채팅 구독 + 미열람 카운트 (SEQ=10211 STEP=10) ─────────
  useEffect(() => {
    const unsub = subscribeRoom(() => setChatLog(roomStore.getChatLog()))
    return () => { unsub() }
  }, [])
  useEffect(() => {
    if (showChat) {
      setUnreadChat(0)
      prevChatLen.current = chatLog.length
      return
    }
    const newCount = chatLog.length - prevChatLen.current
    if (newCount > 0) {
      setUnreadChat(prev => prev + newCount)
      prevChatLen.current = chatLog.length
    }
  }, [chatLog, showChat])

  // ── 채팅 전송 핸들러 (카탄 Play.tsx handleSendChat 미러) ─────
  async function handleSendChat(message: string) {
    try {
      await roomHub.sendChat(roomCode, message)
    } catch {
      setErrorToast('채팅 전송 실패')
      window.setTimeout(() => setErrorToast(null), 3000)
    }
  }

  // ── 에러 토스트 (TtrActionError broadcast) ───────────────
  const [errorToast, setErrorToast] = useState<string | null>(null)
  useEffect(() => {
    const unsub = ticketHub.onActionError(p => {
      const msg = (p?.errorMessage ?? p?.errorCode ?? 'Unknown TTR error')
      setErrorToast(msg)
      window.setTimeout(() => setErrorToast(null), 3500)
    })
    return unsub
  }, [])

  // ── Claim 모달 상태 ─────────────────────────────────────
  const [claimTarget, setClaimTarget] = useState<TtrRoute | null>(null)

  // ── 모바일 탭 nav 상태 (SoT §3-A v2.5 모바일 UX 라운드, SEQ=10211)
  //   <768px 에서만 활성, 데스크톱/태블릿은 CSS로 미표시.
  //   STEP=8 (사용자 요청 "플레이어+보드 합치기"): 3탭 → 2탭 (board / hand).
  //   board 탭이 보드+플레이어 패널 stack 통합, players 탭 deprecated.
  //   default = 'hand' (액션+핸드 통합 진입 우선).
  type MobileTab = 'board' | 'hand'
  const [mobileTab, setMobileTab] = useState<MobileTab>('hand')

  // ── 보드 탭 내 티켓 모달 (SEQ=10211 STEP=4, 사용자 요청)
  //   보드 탭 풀스크린에서 티켓 확인 + 도시 강조 동선 지원.
  //   floating button → 모달 ticket list → 클릭 시 highlightCityIds 동기 + 모달 자동 닫기.
  const [showBoardTickets, setShowBoardTickets] = useState<boolean>(false)

  // ── 모든 티켓 도시 보기 토글 (SEQ=10211 STEP=6, 사용자 요청)
  //   ON 시 본인 myTickets의 모든 cityA·cityB 합집합을 보드 ring으로 강조.
  //   PC TtrHand 헤더 + 모바일 핸드 탭(동일 컴포넌트) + 모바일 보드 탭 모달 3 위치 단일 진리원천 state.
  const [showAllTicketCities, setShowAllTicketCities] = useState<boolean>(false)

  // ── 채팅 — state는 useEffect 위에 hoist 완료(L91~94), 본 위치는 placeholder 주석. */
  // (SEQ=10211 STEP=10: 카탄 Play.tsx 채팅 패턴 미러 — FAB + popover + 미열람 카운트)

  // ── isMobile 감지 (TtrBoard hit area 확장 분기, SEQ=10211 catch #1)
  //   window.matchMedia 사용 — viewport 변경 시 자동 반영.
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  )
  useEffect(() => {
    if (typeof window === 'undefined') return
    const mql = window.matchMedia('(max-width: 767px)')
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  // ── 강조 티켓 (보드 도시 ring) ───────────────────────────
  //   SEQ=10211 STEP=6: showAllTicketCities 토글 ON 시 모든 myTickets cityA·cityB 합집합 우선.
  //   단일 ticket 강조와 mutex(handleTicketRowClick에서 토글 OFF 동반).
  const [highlightedTicketId, setHighlightedTicketId] = useState<number | null>(null)
  const highlightCityIds: number[] | undefined = useMemo(() => {
    if (showAllTicketCities) {
      if (myTickets.length === 0) return undefined
      const set = new Set<number>()
      myTickets.forEach(t => { set.add(t.cityAId); set.add(t.cityBId) })
      return Array.from(set)
    }
    if (highlightedTicketId === null) return undefined
    const t = myTickets.find(tt => tt.ticketId === highlightedTicketId)
    return t ? [t.cityAId, t.cityBId] : undefined
  }, [showAllTicketCities, highlightedTicketId, myTickets])

  // ── 도시 이름 매핑 (모달용) ─────────────────────────────
  const cityNameMap = useMemo(() => {
    const map = new Map<number, string>()
    cities.forEach(c => map.set(c.cityId, c.name))
    return map
  }, [cities])

  // ── 클릭 가능 lane 필터 (본인 턴 + IDLE + 액션 미수행 + Hub 연결) ──
  // SEQ=10199 STEP=1: 한 턴 1액션 가드 — 이미 액션 수행 시 노선 클릭 차단
  // SEQ=10211 STEP=16: hubReady 가드 추가 (연결 미완 시 노선 클릭 차단)
  function isClickableLane(_r: TtrRoute): boolean {
    return hubReady && isMyTurn && subPhase === 'IDLE' && !actionDoneThisTurn
  }

  // ── 본인 핸드 카드 합계 (PlayersPanel용) ─────────────────
  const myHandTotalCount = useMemo(() =>
    Object.values(myHand).reduce((s, v) => s + (v || 0), 0),
  [myHand])

  // ── 액션 핸들러 ─────────────────────────────────────────
  // SEQ=10211 STEP=16 (가설 A): hubReady 미완 시 안내 토스트 + invoke 차단.
  function guardHub(): boolean {
    if (!hubReady) {
      setErrorToast('서버 연결 중... 잠시 후 다시 시도해주세요')
      window.setTimeout(() => setErrorToast(null), 2500)
      return false
    }
    return true
  }
  function handleDrawFromDeck() {
    if (!effectiveMatchId || !isMyTurn || !guardHub()) return
    ticketHub.drawTrainCard(effectiveMatchId, -1).catch(e => console.error('[PlayTicket] drawFromDeck', e))
  }
  function handleDrawFromFaceUp(slotIndex: number) {
    if (!effectiveMatchId || !isMyTurn || !guardHub()) return
    ticketHub.drawTrainCard(effectiveMatchId, slotIndex).catch(e => console.error('[PlayTicket] drawFromFaceUp', e))
  }
  function handleDrawTickets() {
    if (!effectiveMatchId || !isMyTurn || !guardHub()) return
    ticketHub.drawTickets(effectiveMatchId).catch(e => console.error('[PlayTicket] drawTickets', e))
  }
  function handleKeepTickets(keepIds: number[]) {
    if (!effectiveMatchId || !guardHub()) return
    // 낙관적 로컬 반영 — 서버 broadcast 도착 전 UX 즉시 반영
    ticketStore.applyMyTicketsKeptLocal(keepIds)
    ticketHub.keepTickets(effectiveMatchId, keepIds.join(',')).catch(e => {
      console.error('[PlayTicket] keepTickets', e)
      // 실패 시 토스트로 알림 — 서버 재요청은 사용자가 GameLoad(추후) 또는 새로고침
      setErrorToast('티켓 keep 전송 실패 (재시도 필요)')
    })
  }
  function handleEndTurn() {
    if (!effectiveMatchId || !isMyTurn || !guardHub()) return
    ticketHub.endTurn(effectiveMatchId).catch(e => console.error('[PlayTicket] endTurn', e))
  }
  /**
   * catch #16 fix (SEQ=10202 STEP=1, 2026-05-26): TTR 게임 포기 버튼.
   *   카탄 `Play.tsx:handleForfeitGame` 패턴 미러 — BG_ROOM_MEMBER row 영구 삭제 (재진입 불가).
   *   다른 플레이어는 빈 자리 그대로 게임 계속 (USA 원작 룰 외 자체 정책 — Phase 후반 보강).
   */
  async function handleForfeitGame() {
    if (!window.confirm(
      '게임을 포기하시겠습니까?\n' +
      '\n자리가 영구 정리되어 재진입 불가합니다.\n' +
      '다른 플레이어는 빈 자리 그대로 게임을 계속합니다.'
    )) return
    try {
      await roomHub.roomLeave(roomCode)
    } catch {
      // invoke 실패해도 클라 측 정리는 진행 (서버 측 grace 후 자동 정리)
    }
    ticketStore.clearGame()
    await ticketHub.stopConnection().catch(() => {})
    onLeave()
  }
  function handleRouteClick(r: TtrRoute) {
    if (!isClickableLane(r)) return
    // SEQ=10211 STEP=27 (사용자 catch "Double-route second lane disabled for 2-3 players"):
    //   USA 클래식 룰 (SoT §2-1): 2~3인 게임에서 더블 노선은 한쪽 lane만 점유 가능 (다른 lane 영구 차단).
    //   SP가 검증 + SIGNAL 던지지만, 클라 미리 차단 + 명확 안내 (옵션 A — 룰 보존 + 시각 강화).
    if (r.isDouble && players.length <= 3) {
      const otherLaneClaimed = claims.some(c => c.routeId === r.routeId && c.laneNo !== r.laneNo)
      if (otherLaneClaimed) {
        setErrorToast('2~3인 게임 — 더블 노선 다른 lane 점유 중, 본 lane 영구 차단 (USA 클래식 룰)')
        window.setTimeout(() => setErrorToast(null), 3500)
        return
      }
    }
    setClaimTarget(r)
  }
  function handleClaimConfirm(colorUsed: string, wildCount: number) {
    if (!effectiveMatchId || !claimTarget || !guardHub()) return
    const r = claimTarget
    setClaimTarget(null)
    ticketHub.claimRoute(effectiveMatchId, r.routeId, r.laneNo, colorUsed, wildCount)
      .catch(e => console.error('[PlayTicket] claimRoute', e))
  }
  function handleTicketRowClick(ticketId: number) {
    // SEQ=10211 STEP=6: 단일 ticket 클릭 = 전체 보기 토글 OFF + 단일 강조 진입 (mutex).
    if (showAllTicketCities) setShowAllTicketCities(false)
    setHighlightedTicketId(prev => prev === ticketId ? null : ticketId)
  }
  function handleToggleAllTicketCities() {
    setShowAllTicketCities(v => !v)
    // 전체 보기 ON 진입 시 단일 강조 해제 (시각 충돌 방지).
    if (!showAllTicketCities) setHighlightedTicketId(null)
  }

  // ── 로딩 가드 ───────────────────────────────────────────
  if (!game || cities.length === 0 || routes.length === 0) {
    return (
      <div className="ttr-play-loading">
        <p>티켓 투 라이드 — 게임/보드 로딩 중...</p>
        <small>방 {roomCode} · 매치 {matchId ?? 0}</small>
        <div>
          <button onClick={onLeave}>나가기</button>
          <button onClick={onLogout}>로그아웃</button>
        </div>
      </div>
    )
  }

  // ── Pending 모달 자동 표시 분기 ─────────────────────────
  //   AWAIT_TICKET_KEEP + 펜딩 티켓 있을 때 모달 강제 노출
  const showTicketKeepModal = subPhase === 'AWAIT_TICKET_KEEP' && myPending.length > 0
  const ticketKeepContext: 'SETUP' | 'MAIN' = game.status === 'DEAL_TICKETS' ? 'SETUP' : 'MAIN'

  return (
    <div className="ttr-play" data-my-turn={isMyTurn ? '1' : '0'}>
      {/* ── 상단 헤더 ──
           SEQ=10211 (모바일 UX 라운드): 모바일 <768px에서 정보 요소에 data-mobile-hide 표시 →
           CSS @media에서 display: none. 보드 수직 공간 확보 (catch #3). */}
      <header className="ttr-header">
        <span>방 {roomCode}</span>
        <span data-mobile-hide="1">매치 {effectiveMatchId ?? 0}</span>
        <span>턴 {game.turnNo} · {game.status}</span>
        <span className={isMyTurn ? 'ttr-header-myturn-on' : 'ttr-header-myturn-off'}>{isMyTurn ? '본인 턴' : '대기'}</span>
        {/* SEQ=10211 STEP=16 (가설 A): hubReady 미완 시 시각 안내 — 사용자 새로고침 직후 button 무반응 인지 회피 */}
        {!hubReady && <span className="ttr-header-hub-connecting" title="서버 연결 중">🔄 연결 중</span>}
        <span data-mobile-hide="1">덱 {deckRemain} · 티켓덱 {ticketDeckRemain}</span>
        {/* SEQ=10213 STEP=1: 사운드 토글 — localStorage 'board.muted' 영구화 */}
        <button
          className="ttr-btn-sound"
          onClick={handleToggleMute}
          title={muted ? '사운드 켜기' : '사운드 끄기'}
          aria-label={muted ? '사운드 켜기' : '사운드 끄기'}
        >
          {muted ? '🔇' : '🔊'}
        </button>
        <button onClick={onLeave}>나가기</button>
        {/* catch #16 (SEQ=10202 STEP=1): 게임 포기 — 자리 영구 정리 (재진입 불가, 카탄 Play.tsx 미러) */}
        <button className="ttr-btn-forfeit" onClick={handleForfeitGame}>게임 포기</button>
        <button onClick={onLogout}>로그아웃</button>
      </header>

      {/* ── 본문 레이아웃 (보드 + 좌우 패널) ──
           모바일 탭: data-mobile-tab 속성으로 CSS가 현재 활성 패널만 표시.
           데스크톱/태블릿에서는 CSS가 속성 무시 → 4컬럼/3컬럼 그대로. */}
      <div className="ttr-play-body" data-mobile-tab={mobileTab}>
        {/* 좌: 플레이어 패널 (+ PC 채팅 stack, SEQ=10211 STEP=12 사용자 요청) */}
        <aside className="ttr-left-pane">
          <TtrPlayersPanel
            players={players}
            currentTurnUserCode={game.currentTurnUserCode}
            finalTurnTriggerUserCode={game.finalTurnTriggerUserCode}
            myUserCode={authState.userCode}
            otherCounts={otherCounts}
            myHandTotalCount={myHandTotalCount}
            myTicketCount={myTickets.length}
          />
          {/* PC 채팅 — 플레이어 패널 하단 stack (모바일은 FAB+popover 별도) */}
          {!isMobile && (
            <div className="ttr-chat-pc-wrap">
              <ChatPanel
                chatLog={chatLog}
                roomCode={roomCode}
                onSend={handleSendChat}
                onClear={() => roomStore.clearChatLog()}
                tabActive
              />
            </div>
          )}
        </aside>

        {/* 중: 보드 */}
        <main className="ttr-board-wrap">
          <TtrBoard
            width={BOARD_WIDTH}
            height={BOARD_HEIGHT}
            cities={cities}
            routes={routes}
            claims={claims}
            players={players}
            clickableLanes={isClickableLane}
            highlightCityIds={highlightCityIds}
            recentClaim={recentClaim}
            isMobile={isMobile}
            /* SEQ=10211 STEP=8: 모바일 hand 탭에서는 zoom controls JSX 자체 unmount(fixed lingering 차단) */
            showZoomControls={isMobile && mobileTab !== 'hand'}
            onRouteClick={handleRouteClick}
            onCityClick={(_cid) => { /* 향후: 도시 → 인접 노선 ring */ }}
          />
        </main>

        {/* 우: 액션 + 핸드 */}
        <aside className="ttr-right-pane">
          <TtrActionPanel
            isMyTurn={isMyTurn}  /* STEP=17: hubReady는 onClick 가드에서 처리, prop은 게임 권위 상태만 */
            subPhase={subPhase}
            faceUp={faceUp}
            deckRemain={deckRemain}
            discardRemain={ticketStore.getDiscardRemain()}
            ticketDeckRemain={ticketDeckRemain}
            actionDoneThisTurn={actionDoneThisTurn}
            onDrawFromFaceUp={handleDrawFromFaceUp}
            onDrawFromDeck={handleDrawFromDeck}
            onDrawTickets={handleDrawTickets}
            onEndTurn={handleEndTurn}
            /* STEP=17: button disabled 시 사용자에게 reason 토스트 (먹통 인지 회피) */
            onDisabledReason={(reason) => {
              setErrorToast(reason)
              window.setTimeout(() => setErrorToast(null), 2500)
            }}
          />
          <TtrHand
            myHand={myHand}
            myTickets={myTickets}
            cities={cities}
            highlightedTicketId={highlightedTicketId}
            showAllTicketCities={showAllTicketCities}
            onTicketClick={handleTicketRowClick}
            onToggleAllTicketCities={handleToggleAllTicketCities}
          />
        </aside>
      </div>

      {/* ── 모바일 2탭 nav (SEQ=10211 STEP=8: 플레이어+보드 통합 → 2탭).
           본인 턴 강조: 핸드 탭이 active 아닐 때 'attention' dot pulse. */}
      <nav className="ttr-mobile-tabnav" role="tablist" aria-label="보드/핸드 탭">
        <button
          role="tab"
          aria-selected={mobileTab === 'board'}
          className={'ttr-mobile-tab' + (mobileTab === 'board' ? ' active' : '')}
          onClick={() => setMobileTab('board')}
        >
          보드 + 플레이어
        </button>
        <button
          role="tab"
          aria-selected={mobileTab === 'hand'}
          className={
            'ttr-mobile-tab' +
            (mobileTab === 'hand' ? ' active' : '') +
            (isMyTurn && mobileTab !== 'hand' ? ' my-turn-attention' : '')
          }
          onClick={() => setMobileTab('hand')}
        >
          핸드
        </button>
      </nav>

      {/* ── 모바일 채팅 FAB + popover (SEQ=10211 STEP=12: PC는 좌측 패널 stack으로 이동)
           PC에선 isMobile=false라 미렌더. 모바일만 FAB+popover. */}
      {isMobile && (
        <>
          <button
            className={'ttr-chat-fab' + (showChat ? ' active' : '')}
            onClick={() => setShowChat(s => !s)}
            aria-label={'채팅' + (unreadChat > 0 ? ` (안 읽음 ${unreadChat})` : '')}
            title={showChat ? '채팅 닫기' : '채팅 열기'}
          >
            <span aria-hidden="true">💬</span>
            {unreadChat > 0 && <span className="ttr-chat-fab-badge">{unreadChat > 99 ? '99+' : unreadChat}</span>}
          </button>
          {showChat && (
            <div className="ttr-chat-popover" role="dialog" aria-label="채팅">
              <ChatPanel
                chatLog={chatLog}
                roomCode={roomCode}
                onSend={handleSendChat}
                onClear={() => roomStore.clearChatLog()}
                tabActive
              />
            </div>
          )}
        </>
      )}

      {/* ── 보드 탭 내 티켓 floating button + 모달 (SEQ=10211 STEP=4, 사용자 요청) ──
           모바일 보드 탭 풀스크린에서 티켓 확인 + 도시 강조 동선.
           PC·태블릿·다른 탭에서는 isMobile/탭 조건으로 미렌더. */}
      {isMobile && mobileTab === 'board' && (
        <button
          className="ttr-board-tickets-fab"
          onClick={() => setShowBoardTickets(s => !s)}
          aria-label={'내 티켓 ' + myTickets.length + '장'}
          title="내 티켓 보기"
        >
          <span aria-hidden="true">📋</span>
          <span className="ttr-board-tickets-fab-count">{myTickets.length}</span>
        </button>
      )}
      {isMobile && mobileTab === 'board' && showBoardTickets && (
        <div
          className="ttr-modal-overlay ttr-board-tickets-overlay"
          onClick={() => setShowBoardTickets(false)}
          role="presentation"
        >
          <div
            className="ttr-modal ttr-board-tickets-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-label="내 티켓"
          >
            <div className="ttr-board-tickets-modal-header">
              <h3>내 티켓 ({myTickets.length}장)</h3>
              {/* SEQ=10211 STEP=6: 모든 티켓 도시 보기 토글 (보드 모달, PC TtrHand와 단일 진리원천 state 공유) */}
              <button
                type="button"
                className={'ttr-show-all-cities-btn' + (showAllTicketCities ? ' active' : '')}
                onClick={handleToggleAllTicketCities}
                disabled={myTickets.length === 0}
                title={showAllTicketCities ? '전체 도시 강조 끄기' : '본인 티켓 전체 도시 보드에 표시'}
              >
                {showAllTicketCities ? '📍 전체 끄기' : '📍 전체 도시'}
              </button>
            </div>
            <ul className="ttr-my-ticket-list">
              {myTickets.length === 0 && <li className="ttr-empty">티켓 없음</li>}
              {myTickets.map(t => (
                <li
                  key={t.ticketId}
                  className={
                    'ttr-my-ticket-row' +
                    (t.isCompleteUx ? ' complete' : ' incomplete') +
                    (highlightedTicketId === t.ticketId ? ' highlighted' : '')
                  }
                  onClick={() => {
                    handleTicketRowClick(t.ticketId)
                    setShowBoardTickets(false)
                  }}
                  title={t.isCompleteUx ? '연결됨 — 보드 도시 강조' : '미연결 — 보드 도시 강조'}
                >
                  <span className="ttr-ticket-points">{t.points}</span>
                  <span className="ttr-ticket-cities">
                    {cityNameMap.get(t.cityAId) ?? `#${t.cityAId}`} ↔ {cityNameMap.get(t.cityBId) ?? `#${t.cityBId}`}
                  </span>
                  <span className={'ttr-ticket-status ' + (t.isCompleteUx ? 'ok' : 'ng')}>
                    {t.isCompleteUx ? 'OK' : 'X'}
                  </span>
                </li>
              ))}
            </ul>
            <div className="ttr-board-tickets-modal-actions">
              <button onClick={() => setShowBoardTickets(false)}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* ── 모달 (오버레이) ── */}
      {claimTarget && (
        <TtrClaimRouteModal
          route={claimTarget}
          cityAName={cityNameMap.get(claimTarget.cityAId) ?? `#${claimTarget.cityAId}`}
          cityBName={cityNameMap.get(claimTarget.cityBId) ?? `#${claimTarget.cityBId}`}
          myHand={myHand}
          onConfirm={handleClaimConfirm}
          onCancel={() => setClaimTarget(null)}
        />
      )}

      {showTicketKeepModal && (
        <TtrTicketKeepModal
          context={ticketKeepContext}
          pending={myPending}
          cities={cities}
          onConfirm={handleKeepTickets}
        />
      )}

      {gameEnded && (
        <TtrGameEndModal
          endPayload={gameEnded}
          players={players}
          cities={cities}
          onClose={() => {
            ticketStore.clearGame()
            onLeave()
          }}
        />
      )}

      {/* 에러 토스트 */}
      {errorToast && (
        <div className="ttr-error-toast" onClick={() => setErrorToast(null)}>
          {errorToast}
        </div>
      )}
    </div>
  )
}

export default PlayTicket
